import type { Equipment, SetStatus, UnitSystem } from '@lift2fit/shared-types';
import type { SetMeasure } from '@/lib/prescription';
import type { BestMap } from '@/lib/prEngine';

export interface DemoSetSeed {
  weightKg: number;
  reps: number;
  rpe: number | null;
  status: SetStatus;
  isWarmup?: boolean;
  measure?: SetMeasure;
  distanceM?: number | null;
  durationS?: number | null;
  painScore?: number | null;
}

export interface DemoRowSeed {
  id: string;
  exerciseId: string;
  movement: string;
  variant: string | null;
  muscleGroup: string;
  equipment: Equipment;
  modifications: string | null;
  isCompound: boolean;
  measure: SetMeasure;
  perSide: boolean;
  isBodyweight: boolean;
  tempo: string | null;
  supersetGroup: number | null;
  prescribed: {
    sets: number;
    repMin: number;
    repMax: number;
    targetRpe: number | null;
    targetWeightKg: number | null;
    targetDistanceM?: number | null;
    restSeconds: number;
  };
  coachCue: string | null;
  notes: string;
  sets: DemoSetSeed[];
}

export interface DemoSession {
  label: string;
  week: number;
  dayIndex: number;
  focus: string;
  startedAt: string;
  /** The spreadsheet's POST SESSION SCORE, captured in-app: mood 1-5 + session RPE. */
  postSession: { mood: number | null; rpe: number | null; pain: number | null };
  rows: DemoRowSeed[];
}

/**
 * DAY 1 GYM · LOWER, transcribed from the supplied trainer block
 * ("Mahesh 1.1-1.6 — BLOCK 1.5"). Two sets are already logged and one is open on
 * the primary lift, so a visitor can log it and watch the PR engine fire.
 *
 * Note the vocabulary the sheet uses and the Grid now carries: superset groups,
 * per-side reps, bodyweight loads, prescribed tempo and an RPE target.
 */
export const DEMO_SESSION: DemoSession = {
  label: 'DAY 1 GYM',
  focus: 'LOWER',
  week: 1,
  dayIndex: 1,
  startedAt: '2026-05-12T18:04:00.000Z',
  postSession: { mood: 4, rpe: 7, pain: 0 },
  rows: [
    {
      id: 'se-squat-ssb',
      exerciseId: 'e0000000-0000-4000-8000-000000000012',
      movement: 'SQUAT',
      variant: 'SSB',
      muscleGroup: 'quads',
      equipment: 'barbell',
      modifications: null,
      isCompound: true,
      measure: 'reps',
      perSide: false,
      isBodyweight: false,
      tempo: '3-1-1-0',
      supersetGroup: 1,
      prescribed: { sets: 3, repMin: 8, repMax: 8, targetRpe: 7, targetWeightKg: 100, restSeconds: 180 },
      coachCue: 'Hit 3 × 8 at RPE ≤ 7.5 and the coach takes you to 102.5 kg next block.',
      notes: 'Brace, sit between the hips. No bounce out of the hole.',
      sets: [
        { weightKg: 60, reps: 10, rpe: 6, status: 'done' },
        { weightKg: 100, reps: 8, rpe: 7, status: 'done' },
        { weightKg: 100, reps: 8, rpe: null, status: 'pending' },
      ],
    },
    {
      id: 'se-hip-airplane',
      exerciseId: 'e0000000-0000-4000-8000-000000000026',
      movement: 'HIP AIRPLANE',
      variant: null,
      muscleGroup: 'glutes',
      equipment: 'bodyweight',
      modifications: null,
      isCompound: false,
      measure: 'reps',
      perSide: true,
      isBodyweight: true,
      tempo: null,
      supersetGroup: 1,
      prescribed: { sets: 3, repMin: 6, repMax: 6, targetRpe: 7, targetWeightKg: 0, restSeconds: 60 },
      coachCue: 'Own the end range before you load it. This is what keeps the squat pain-free.',
      notes: 'Slow. Hips square to the floor.',
      // two of three sets logged — the Grid shows the missing one as a one-tap cell
      sets: [
        { weightKg: 0, reps: 6, rpe: 6, status: 'done' },
        { weightKg: 0, reps: 6, rpe: 7, status: 'done' },
      ],
    },
    {
      id: 'se-split-squat',
      exerciseId: 'e0000000-0000-4000-8000-000000000017',
      movement: 'SPLIT SQUAT',
      variant: 'BULGARIAN / RFE',
      muscleGroup: 'quads',
      equipment: 'barbell',
      modifications: 'BARBELL',
      isCompound: true,
      measure: 'reps',
      perSide: true,
      isBodyweight: false,
      tempo: null,
      supersetGroup: 2,
      prescribed: { sets: 3, repMin: 6, repMax: 6, targetRpe: 7, targetWeightKg: 40, restSeconds: 120 },
      coachCue: 'Front shin vertical, weight through the mid-foot. Two more kilos is earned, not given.',
      notes: 'Rear foot elevated. Control the descent.',
      sets: [
        { weightKg: 20, reps: 8, rpe: 6, status: 'done' },
        { weightKg: 40, reps: 8, rpe: 7.5, status: 'done' },
        { weightKg: 40, reps: 6, rpe: null, status: 'pending' },
      ],
    },
    {
      id: 'se-rdl-single',
      exerciseId: 'e0000000-0000-4000-8000-000000000027',
      movement: 'RDL',
      variant: 'SINGLE LEG',
      muscleGroup: 'hamstrings',
      equipment: 'dumbbell',
      modifications: 'DB',
      isCompound: true,
      measure: 'reps',
      perSide: true,
      isBodyweight: false,
      tempo: '4-0-1-0',
      supersetGroup: 3,
      prescribed: { sets: 3, repMin: 8, repMax: 8, targetRpe: 7, targetWeightKg: 15, restSeconds: 90 },
      coachCue: 'Hinge, do not squat. Slow eccentric is the whole point of the tempo.',
      notes: 'Hips square. Bar close to the leg.',
      sets: [
        { weightKg: 15, reps: 8, rpe: 6.5, status: 'done' },
        { weightKg: 15, reps: 8, rpe: 7, status: 'done' },
        { weightKg: 15, reps: 8, rpe: null, status: 'pending' },
      ],
    },
    {
      id: 'se-heel-raise',
      exerciseId: 'e0000000-0000-4000-8000-000000000018',
      movement: 'HEEL RAISE',
      variant: 'BILATERAL',
      muscleGroup: 'calves',
      equipment: 'machine',
      modifications: 'SSB / ON BOX / SINGLE LEG',
      isCompound: false,
      measure: 'reps',
      perSide: false,
      isBodyweight: false,
      tempo: null,
      supersetGroup: 3,
      prescribed: { sets: 3, repMin: 10, repMax: 10, targetRpe: 7, targetWeightKg: 62.5, restSeconds: 60 },
      coachCue: 'Full stretch at the bottom, two-second pause at the top. No bouncing.',
      notes: 'On a box for full range.',
      sets: [
        { weightKg: 60, reps: 10, rpe: 7, status: 'done' },
        { weightKg: 60, reps: 10, rpe: 7, status: 'done' },
        { weightKg: 62.5, reps: 10, rpe: null, status: 'pending' },
      ],
    },
  ],
};

/**
 * Best estimated 1RM per exercise/rep-bucket from the previous block.
 * Beating these is what makes the PR tag fire.
 */
export const DEMO_PRIOR_BESTS: BestMap = {
  // squat 8s at 95 kg last block -> e1RM 120.3
  'e0000000-0000-4000-8000-000000000012:7-10': 120.33,
  // split squat 6s at 37.5 kg -> 45
  'e0000000-0000-4000-8000-000000000017:4-6': 45,
  // single-leg RDL 8s at 15 kg -> 19
  'e0000000-0000-4000-8000-000000000027:7-10': 19,
  // heel raise 10s at 60 kg -> 80
  'e0000000-0000-4000-8000-000000000018:7-10': 80,
};

export const DEMO_STREAK_WEEKS = 12;

/** The Grid opens in the saved unit preference; kg is the canonical store. */
export const DEMO_UNIT: UnitSystem = 'metric';
